import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import AdminLayout from './Layout';
import { authFetch, uploadFile } from '../../utils/auth';
import {
  Save,
  Eye,
  Image as ImageIcon,
  X,
  Upload,
  FileText,
  FileImage,
  Tag,
  Folder,
  Globe,
  ArrowLeft,
  Loader,
  Paperclip,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'react-toastify';
import 'react-quill/dist/quill.snow.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CATEGORIES = [
  'Income Tax', 'GST', 'Corporate Law', 'Audit & Assurance',
  'Financial Planning', 'Compliance', 'Budget & Finance',
  'Investment Advisory', 'ROC & MCA', 'TDS & TCS',
  'International Taxation', 'Startups',
  'News', 'Case Study', 'Opinion', 'Other',
];

const ACCEPT_FILES = 'image/jpeg,image/png,image/gif,image/webp,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const inputCls  = 'w-full px-3 py-2 rounded-xl border text-sm outline-none transition-all';
const inputStyle = { borderColor: '#BFC9D1', backgroundColor: '#EAEFEF', color: '#25343F' };
const onFocusIn  = e => { e.target.style.borderColor = '#FF9B51'; e.target.style.boxShadow = '0 0 0 3px rgba(255,155,81,0.12)'; };
const onFocusOut = e => { e.target.style.borderColor = '#BFC9D1'; e.target.style.boxShadow = 'none'; };

const EditBlog = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const quillRef    = useRef(null);
  const attachRef   = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [formData, setFormData] = useState({
    title:    '',
    content:  '',
    excerpt:  '',
    category: 'Income Tax',
    tags:     [],
    keywords: '',
    type:     'blog',
    isActive: false,
    seo: { metaTitle: '', metaDescription: '', metaKeywords: '' },
  });

  const [featuredImage,        setFeaturedImage]        = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [attachments,          setAttachments]          = useState([]);
  const [uploadingAttach,      setUploadingAttach]      = useState(false);
  const [tagInput,             setTagInput]             = useState('');
  const [showSEO,              setShowSEO]              = useState(false);

  useEffect(() => {
    fetchBlog();
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBlog = async () => {
    try {
      const response = await authFetch(`/api/blogs/${slug}`);
      if (response) {
        const data = await response.json();
        if (data.success) {
          const blog = data.data;
          setFormData({
            title:    blog.title    || '',
            content:  blog.content  || '',
            excerpt:  blog.excerpt  || '',
            category: blog.category || 'Income Tax',
            tags:     blog.tags     || [],
            keywords: blog.keywords || '',
            type:     blog.type     || 'blog',
            isActive: blog.isActive || false,
            seo: {
              metaTitle:       blog.seo?.metaTitle       || '',
              metaDescription: blog.seo?.metaDescription || '',
              metaKeywords:    blog.seo?.metaKeywords    || '',
            },
          });

          if (blog.featuredImage) {
            setFeaturedImage(blog.featuredImage);
            setFeaturedImagePreview(`${API_URL}/api/upload/file/${blog.featuredImage.fileId}`);
          }

          if (blog.attachments?.length) {
            setAttachments(blog.attachments.map((a, i) => ({ ...a, _key: `existing-${i}` })));
          }
        } else {
          toast.error('Blog not found');
          navigate('/admin/blogs');
        }
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      toast.error('Failed to load blog');
      navigate('/admin/blogs');
    } finally {
      setLoading(false);
    }
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['link', 'image'],
        [{ 'align': [] }],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'blockquote', 'code-block',
    'list', 'bullet', 'indent',
    'link', 'image',
    'align'
  ];

  function imageHandler() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (file) {
        try {
          toast.info('Uploading image...');
          const result = await uploadFile(file, 'image');
          
          if (result.success) {
            const quill = quillRef.current.getEditor();
            const range = quill.getSelection(true);
            const imageUrl = `${process.env.REACT_APP_API_URL}${result.data.url}`;
            quill.insertEmbed(range.index, 'image', imageUrl);
            quill.setSelection(range.index + 1);
            toast.success('Image uploaded successfully');
          } else {
            toast.error('Failed to upload image');
          }
        } catch (error) {
          console.error('Image upload error:', error);
          toast.error('Failed to upload image');
        }
      }
    };
  }

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSEOChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      seo: {
        ...prev.seo,
        [name]: value
      }
    }));
  };

  const handleFeaturedImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFeaturedImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      try {
        toast.info('Uploading featured image...');
        const result = await uploadFile(file, 'image');
        
        if (result.success) {
          setFeaturedImage({
            fileId: result.data.fileId,
            filename: result.data.filename,
            contentType: result.data.contentType,
            size: result.data.size
          });
          toast.success('Featured image uploaded');
        } else {
          toast.error('Failed to upload featured image');
        }
      } catch (error) {
        console.error('Featured image upload error:', error);
        toast.error('Failed to upload featured image');
      }
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e, publish = false) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    if (!formData.content.trim() || formData.content === '<p><br></p>') {
      toast.error('Please enter content');
      return;
    }

    setSaving(true);

    try {
      const blogData = {
        ...formData,
        featuredImage,
        attachments: attachments.map(({ _key, ...rest }) => rest),
        isActive: publish !== undefined ? publish : formData.isActive,
        excerpt: formData.excerpt || formData.content.replace(/<[^>]*>/g, '').substring(0, 200) + '...'
      };

      const response = await authFetch(`/api/blogs/${slug}`, {
        method: 'PUT',
        body: JSON.stringify(blogData)
      });

      if (response) {
        const data = await response.json();
        if (data.success) {
          toast.success('Blog updated successfully!');
          setTimeout(() => navigate('/admin/blogs'), 1500);
        } else {
          toast.error(data.message || 'Failed to update blog');
        }
      }
    } catch (error) {
      console.error('Error updating blog:', error);
      toast.error('Failed to update blog');
    } finally {
      setSaving(false);
    }
  };

  /* ── Attachment handlers ── */
  const handleAttachmentFiles = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingAttach(true);
    for (const file of files) {
      const type = file.type.startsWith('image/') ? 'image' : 'document';
      try {
        toast.info(`Uploading ${file.name}…`);
        const result = await uploadFile(file, type);
        if (result.success) {
          setAttachments(prev => [...prev, {
            _key:        `${Date.now()}-${Math.random()}`,
            fileId:      result.data.fileId,
            filename:    result.data.filename,
            originalName: result.data.originalName || file.name,
            contentType: result.data.contentType,
            size:        result.data.size,
            label:       file.name.replace(/\.[^/.]+$/, ''),
          }]);
          toast.success(`${file.name} uploaded`);
        } else {
          toast.error(`Failed: ${file.name}`);
        }
      } catch {
        toast.error(`Failed: ${file.name}`);
      }
    }
    setUploadingAttach(false);
    e.target.value = '';
  };

  const removeAttachment = (key) => setAttachments(prev => prev.filter(a => a._key !== key));
  const updateLabel = (key, label) => setAttachments(prev => prev.map(a => a._key === key ? { ...a, label } : a));

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <Loader className="w-12 h-12 text-primary-600 animate-spin mb-4" />
          <p className="text-gray-600">Loading blog...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <style>{`
        .ql-editor            { min-height: 48vh; }
        .ql-toolbar.ql-snow   { border-color: #BFC9D1 !important; border-radius: 12px 12px 0 0; }
        .ql-container.ql-snow { border-color: #BFC9D1 !important; border-radius: 0 0 12px 12px; }
        .ql-toolbar.ql-snow .ql-stroke { stroke: #25343F; }
        .ql-toolbar.ql-snow button:hover .ql-stroke { stroke: #FF9B51; }
        .ql-toolbar.ql-snow button.ql-active .ql-stroke { stroke: #FF9B51; }
      `}</style>

      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/admin/blogs')}
            className="flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-60"
            style={{ color: '#25343F' }}
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => handleSubmit(e, false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-colors hover:bg-white disabled:opacity-50"
              style={{ borderColor: '#BFC9D1', color: '#25343F' }}
            >
              <Save className="w-4 h-4" /> Save Draft
            </button>
            <button
              onClick={(e) => handleSubmit(e, true)}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
              style={{ backgroundColor: '#FF9B51' }}
            >
              {saving
                ? <><span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Saving…</>
                : <><Eye className="w-4 h-4" /> {formData.isActive ? 'Update' : 'Publish'}</>}
            </button>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="flex gap-5 items-start">

          {/* LEFT: Main content */}
          <div className="flex-1 min-w-0 space-y-4">

            {/* Title */}
            <div className="bg-white rounded-2xl border px-6 py-5" style={{ borderColor: '#BFC9D1' }}>
              <input
                type="text"
                placeholder="Blog Title…"
                value={formData.title}
                onChange={e => handleChange('title', e.target.value)}
                className="w-full text-3xl font-bold outline-none border-none bg-transparent"
                style={{ color: '#25343F', fontFamily: 'Georgia, serif' }}
              />
            </div>

            {/* Featured Image */}
            <div className="bg-white rounded-2xl border p-4" style={{ borderColor: '#BFC9D1' }}>
              <p className="text-xs font-semibold mb-3 flex items-center gap-1.5" style={{ color: '#25343F' }}>
                <ImageIcon className="w-3.5 h-3.5" style={{ color: '#FF9B51' }} /> Featured Image
              </p>
              {featuredImagePreview ? (
                <div className="relative group rounded-xl overflow-hidden">
                  <img src={featuredImagePreview} alt="Featured" className="w-full h-52 object-cover" />
                  <button
                    onClick={() => { setFeaturedImage(null); setFeaturedImagePreview(null); }}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  ><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center h-40 rounded-xl border-2 border-dashed cursor-pointer transition-colors hover:border-orange-300" style={{ borderColor: '#BFC9D1' }}>
                  <Upload className="w-8 h-8 mb-2" style={{ color: '#BFC9D1' }} />
                  <p className="text-sm font-medium" style={{ color: '#25343F' }}>Click to upload</p>
                  <p className="text-xs mt-0.5" style={{ color: '#BFC9D1' }}>PNG, JPG, WebP — max 10 MB</p>
                  <input type="file" accept="image/*" onChange={handleFeaturedImageChange} className="hidden" />
                </label>
              )}
            </div>

            {/* Editor */}
            <div className="bg-white rounded-2xl border p-4" style={{ borderColor: '#BFC9D1' }}>
              <p className="text-xs font-semibold mb-3" style={{ color: '#25343F' }}>Content</p>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={formData.content}
                onChange={v => handleChange('content', v)}
                modules={modules}
                formats={formats}
                placeholder="Write your article here…"
              />
            </div>

            {/* Attachments */}
            <div className="bg-white rounded-2xl border p-4" style={{ borderColor: '#BFC9D1' }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#25343F' }}>
                  <Paperclip className="w-3.5 h-3.5" style={{ color: '#FF9B51' }} />
                  Attachments
                  <span className="font-normal" style={{ color: '#BFC9D1' }}>— readers can download these</span>
                </p>
                <button
                  onClick={() => attachRef.current?.click()}
                  disabled={uploadingAttach}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: '#25343F' }}
                >
                  {uploadingAttach
                    ? <><span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" /> Uploading…</>
                    : <><Upload className="w-3 h-3" /> Add Files</>}
                </button>
                <input ref={attachRef} type="file" multiple accept={ACCEPT_FILES} onChange={handleAttachmentFiles} className="hidden" />
              </div>
              {attachments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 rounded-xl border-2 border-dashed cursor-pointer" style={{ borderColor: '#BFC9D1' }} onClick={() => attachRef.current?.click()}>
                  <Paperclip className="w-6 h-6 mb-1.5" style={{ color: '#BFC9D1' }} />
                  <p className="text-xs" style={{ color: '#BFC9D1' }}>PDF, Word, Excel, PowerPoint, or images</p>
                </div>
              ) : (
                <ul className="space-y-2">
                  {attachments.map(att => {
                    const Icon = att.contentType?.startsWith('image/') ? FileImage : FileText;
                    return (
                      <li key={att._key} className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: '#EAEFEF' }}>
                        <Icon className="w-4 h-4 mt-2 flex-shrink-0" style={{ color: '#FF9B51' }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs mb-1.5 truncate" style={{ color: '#BFC9D1' }}>{att.originalName} · {formatSize(att.size)}</p>
                          <input type="text" value={att.label || ''} onChange={e => updateLabel(att._key, e.target.value)} placeholder="Download link label for readers…" className="w-full px-2.5 py-1.5 rounded-lg border text-xs outline-none" style={{ borderColor: '#BFC9D1', backgroundColor: 'white', color: '#25343F' }} />
                        </div>
                        <button onClick={() => removeAttachment(att._key)} className="p-1 rounded-lg text-red-400 hover:bg-red-50 flex-shrink-0 mt-1"><X className="w-3.5 h-3.5" /></button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* RIGHT: Sidebar */}
          <div className="flex-shrink-0 space-y-3" style={{ width: '272px' }}>

            {/* Type & Category */}
            <div className="bg-white rounded-2xl border p-4 space-y-3" style={{ borderColor: '#BFC9D1' }}>
              <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#25343F' }}>
                <Folder className="w-3.5 h-3.5" style={{ color: '#FF9B51' }} /> Classify
              </p>
              <div>
                <label className="block text-xs mb-1" style={{ color: '#BFC9D1' }}>Type</label>
                <select value={formData.type} onChange={e => handleChange('type', e.target.value)} className={inputCls} style={inputStyle} onFocus={onFocusIn} onBlur={onFocusOut}>
                  <option value="blog">Blog</option>
                  <option value="article">Article</option>
                  <option value="news">News</option>
                  <option value="tutorial">Tutorial</option>
                  <option value="case-study">Case Study</option>
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: '#BFC9D1' }}>Category</label>
                <select value={formData.category} onChange={e => handleChange('category', e.target.value)} className={inputCls} style={inputStyle} onFocus={onFocusIn} onBlur={onFocusOut}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-2xl border p-4" style={{ borderColor: '#BFC9D1' }}>
              <p className="text-xs font-semibold flex items-center gap-1.5 mb-3" style={{ color: '#25343F' }}>
                <Tag className="w-3.5 h-3.5" style={{ color: '#FF9B51' }} /> Tags
              </p>
              <div className="flex gap-2 mb-2">
                <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddTag())} placeholder="Add tag…" className={`${inputCls} flex-1`} style={inputStyle} onFocus={onFocusIn} onBlur={onFocusOut} />
                <button onClick={handleAddTag} className="px-3 py-2 rounded-xl text-xs font-semibold text-white" style={{ backgroundColor: '#FF9B51' }}>Add</button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {formData.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255,155,81,0.12)', color: '#FF9B51' }}>
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)} className="hover:opacity-70"><X className="w-2.5 h-2.5" /></button>
                  </span>
                ))}
              </div>
            </div>

            {/* Excerpt */}
            <div className="bg-white rounded-2xl border p-4" style={{ borderColor: '#BFC9D1' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: '#25343F' }}>
                Excerpt <span className="font-normal" style={{ color: '#BFC9D1' }}>(optional)</span>
              </p>
              <textarea value={formData.excerpt} onChange={e => handleChange('excerpt', e.target.value)} placeholder="Short description — auto-generated if empty" rows={3} className="w-full px-3 py-2 rounded-xl border text-xs resize-none outline-none" style={{ borderColor: '#BFC9D1', backgroundColor: '#EAEFEF', color: '#25343F' }} onFocus={onFocusIn} onBlur={onFocusOut} />
            </div>

            {/* SEO */}
            <div className="bg-white rounded-2xl border p-4" style={{ borderColor: '#BFC9D1' }}>
              <button onClick={() => setShowSEO(s => !s)} className="w-full flex items-center justify-between">
                <p className="text-xs font-semibold flex items-center gap-1.5" style={{ color: '#25343F' }}>
                  <Globe className="w-3.5 h-3.5" style={{ color: '#FF9B51' }} /> SEO & OG Tags
                </p>
                {showSEO ? <ChevronUp className="w-3.5 h-3.5" style={{ color: '#BFC9D1' }} /> : <ChevronDown className="w-3.5 h-3.5" style={{ color: '#BFC9D1' }} />}
              </button>
              {showSEO && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="flex items-center justify-between text-xs mb-1" style={{ color: '#BFC9D1' }}>
                      <span>Meta Title</span>
                      <span style={{ color: formData.seo.metaTitle.length > 55 ? '#ef4444' : '#BFC9D1' }}>{formData.seo.metaTitle.length}/60</span>
                    </label>
                    <input type="text" value={formData.seo.metaTitle} onChange={e => handleSEOChange('metaTitle', e.target.value)} maxLength={60} placeholder="SEO title" className={inputCls} style={inputStyle} onFocus={onFocusIn} onBlur={onFocusOut} />
                  </div>
                  <div>
                    <label className="flex items-center justify-between text-xs mb-1" style={{ color: '#BFC9D1' }}>
                      <span>Meta Description</span>
                      <span style={{ color: formData.seo.metaDescription.length > 150 ? '#ef4444' : '#BFC9D1' }}>{formData.seo.metaDescription.length}/160</span>
                    </label>
                    <textarea value={formData.seo.metaDescription} onChange={e => handleSEOChange('metaDescription', e.target.value)} maxLength={160} placeholder="SEO description" rows={3} className="w-full px-3 py-2 rounded-xl border text-xs resize-none outline-none" style={{ borderColor: '#BFC9D1', backgroundColor: '#EAEFEF', color: '#25343F' }} onFocus={onFocusIn} onBlur={onFocusOut} />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: '#BFC9D1' }}>Keywords</label>
                    <input type="text" value={formData.seo.metaKeywords} onChange={e => handleSEOChange('metaKeywords', e.target.value)} placeholder="tax, itr, gst…" className={inputCls} style={inputStyle} onFocus={onFocusIn} onBlur={onFocusOut} />
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EditBlog;